import { useCallback, useMemo } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import type { SortByEnum } from '#src/app/pages/search/shared/context';
import { useSearchActions, useSearchState } from '#src/app/pages/search/shared/searchState';

import { useSortOptions } from './hooks/useSortOptions';

type Result = ModelHookResult<
	{
		options: ReadonlyArray<{ label: string; value: string; subtitle: string }>;
		sortByTitle: string;
		currentRefinement: string;
		currentLabel: string;
	},
	{
		// FIXME(SelectMenu): Using string | number because the component requires Key type from react-aria as value
		changeSelection: (value: string | number | null) => void;
	}
>;

export function useSortSelector(): Result {
	const { translate } = useI18n();

	const sortByTitle = useMemo(() => translate('corgi.search.sort.title'), [translate]);

	const {
		data: { options },
	} = useSortOptions();

	const state = useSearchState();
	const actions = useSearchActions();
	const track = useTracking();

	const currentRefinement = state.sortBy || '';

	const getTrackingContext = useCallback(
		(selectedValue: string) => {
			const selectedOption = options.find(({ value }) => value === selectedValue);
			return selectedOption?.trackingContext || '';
		},
		[options],
	);

	// FIXME(SelectMenu): Using string | number because the component requires Key type from react-aria as value
	const changeSelection = useCallback(
		(selectedKey: string | number | null) => {
			if (selectedKey === null) return;
			const selectedValue = `${selectedKey}` as SortByEnum | '';
			void track('search', { context: getTrackingContext(selectedValue), q: state.query || '' });
			actions.setSortBy(selectedValue || undefined);
		},
		[getTrackingContext, state.query, actions, track],
	);

	const { label: currentLabel } = useMemo(() => {
		const selectedOption = options.find(({ value }) => value === currentRefinement);
		return selectedOption || { label: '' };
	}, [currentRefinement, options]);

	return {
		data: {
			options: options.map(({ label, value, subtitle }) => ({ label, value, subtitle })),
			sortByTitle,
			currentRefinement,
			currentLabel,
		},
		actions: {
			changeSelection,
		},
	};
}
